import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Warning, XCircle, ArrowRight, CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DayClosePrerequisite, DayCloseLabels } from "./types";

export interface PrerequisitesChecklistProps {
  prerequisites: readonly DayClosePrerequisite[];
  labels: DayCloseLabels;
  onOpenPrerequisite?: (key: string, route: string | null) => void;
  className?: string;
}

export function PrerequisitesChecklist({
  prerequisites, labels, onOpenPrerequisite, className,
}: PrerequisitesChecklistProps) {
  // Group: blocked → warning → ok
  const sorted = React.useMemo(
    () =>
      [...prerequisites].sort((a, b) => {
        const order: Record<string, number> = { blocked: 0, warning: 1, ok: 2 };
        return order[a.status] - order[b.status];
      }),
    [prerequisites],
  );

  const allOk = sorted.every((p) => p.status === "ok");
  const hasBlocked = sorted.some((p) => p.status === "blocked");

  return (
    <div className={cn("space-y-3", className)}>
      {hasBlocked && (
        <div className="flex items-start gap-2 p-3 rounded-biz-card bg-biz-danger-subtle border border-biz-danger-border text-biz-danger text-sm">
          <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{labels.steps.prerequisites.blocked}</span>
        </div>
      )}
      {allOk && (
        <div className="flex items-start gap-2 p-3 rounded-biz-card bg-biz-success-subtle border border-biz-success-border text-biz-success text-sm">
          <CheckCircle weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{labels.steps.prerequisites.empty}</span>
        </div>
      )}
      <ul className="space-y-2">
        {sorted.map((p) => (
          <PrerequisiteRow
            key={p.key}
            item={p}
            labels={labels}
            onAction={() => onOpenPrerequisite?.(p.key, p.actionRoute)}
          />
        ))}
      </ul>
    </div>
  );
}

function PrerequisiteRow({
  item, labels, onAction,
}: {
  item: DayClosePrerequisite;
  labels: DayCloseLabels;
  onAction: () => void;
}) {
  const [open, setOpen] = React.useState(item.status !== "ok");
  const Icon = item.status === "ok" ? CheckCircle : item.status === "warning" ? Warning : XCircle;
  const tone =
    item.status === "ok"
      ? { box: "bg-biz-surface-elevated border-biz-border", icon: "text-biz-success" }
      : item.status === "warning"
      ? { box: "bg-biz-warning-subtle border-biz-warning-border", icon: "text-biz-warning" }
      : { box: "bg-biz-danger-subtle border-biz-danger-border", icon: "text-biz-danger" };

  const itemLabel = labels.steps.prerequisites.items[item.key] ?? item.label;

  return (
    <li className={cn("rounded-biz-card border", tone.box)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left biz-focus-ring rounded-biz-card"
      >
        <Icon weight={item.status === "ok" ? "regular" : "fill"} className={cn("w-6 h-6 flex-shrink-0", tone.icon)} />
        <span className="flex-1 font-semibold text-biz-foreground">{itemLabel}</span>
        <span className="font-mono-num tabular-nums text-sm font-semibold text-biz-foreground-muted">
          {item.count}
        </span>
        <CaretDown
          className={cn("w-4 h-4 text-biz-foreground-subtle transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 flex items-start justify-between gap-3 text-sm text-biz-foreground-muted">
              <p className="flex-1">{item.detail}</p>
              {item.status !== "ok" && item.actionRoute && (
                <button
                  type="button"
                  onClick={onAction}
                  className="flex items-center gap-1 h-11 px-3 rounded-biz-btn bg-biz-surface-elevated border border-biz-border-strong text-biz-foreground font-semibold text-sm hover:bg-biz-muted biz-focus-ring"
                >
                  {labels.steps.prerequisites.action}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
